import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import {
  BarChart, Bar, XAxis, YAxis, Cell,
  ResponsiveContainer, Tooltip,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import { invalidateTrackedStocksCache } from "../hooks/useTrackedStocks";
import { computeWeightedAvgRate, computeTotalDebtILS } from "../utils/loanMetrics";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";


const CHART_COLORS = ["#635BFF","#0071e3","#10B981","#F59E0B","#EF4444","#06B6D4","#EC4899","#8B5CF6"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatNum(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("he-IL");
}

function formatCompact(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  const num = Number(n);
  const abs = Math.abs(num);
  if (abs >= 1e9) return (num / 1e9).toFixed(2).replace(/\.?0+$/, "") + "B";
  if (abs >= 1e6) return (num / 1e6).toFixed(1).replace(/\.?0+$/, "") + "M";
  if (abs >= 1e4) return (num / 1e3).toFixed(0) + "K";
  return num.toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

function formatPct(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function daysFromNow(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}

function hebrewGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "בוקר טוב";
  if (h < 17) return "צהריים טובים";
  return "ערב טוב";
}

function hebrewDate() {
  return new Date().toLocaleDateString("he-IL", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

// ─── Alert Banner ────────────────────────────────────────────────────────────

function AlertBanner({ loans }) {
  const alerts = useMemo(() => {
    const list = [];
    loans.forEach((loan) => {
      const lender = loan.lender || "הלוואה";
      const days = daysFromNow(loan.maturityDate);
      if (days != null && days < 0) {
        list.push({ id: `mat-${loan._id}`, loanId: loan._id, severity: "error",
          text: `פירעון עבר לפני ${Math.abs(days)} ימים: ${lender}` });
      } else if (days != null && days <= 30) {
        list.push({ id: `mat-${loan._id}`, loanId: loan._id,
          severity: days <= 7 ? "error" : "warning",
          text: `פירעון בעוד ${days} ימים: ${lender}` });
      }
    });
    return list;
  }, [loans]);

  if (alerts.length === 0) {
    return (
      <div className="dash-alert-clear" style={alertStyles.clear}>
        <span style={alertStyles.dot} />
        <span style={alertStyles.clearText}>הכל תקין — אין התראות פעילות</span>
      </div>
    );
  }

  const hasErrors = alerts.some((a) => a.severity === "error");
  return (
    <div style={hasErrors ? alertStyles.error : alertStyles.warning}>
      <span style={alertStyles.icon}>{hasErrors ? "⚠" : "!"}</span>
      <div style={alertStyles.pills}>
        {alerts.map((a) => (
          <Link key={a.id} to={`/loan/${a.loanId}`} className="no-underline"
            style={a.severity === "error" ? alertStyles.pillError : alertStyles.pillWarning}>
            {a.text} ←
          </Link>
        ))}
      </div>
    </div>
  );
}

const alertStyles = {
  clear: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 18px", borderRadius: "var(--radius)",
    border: "1px solid rgba(52,199,89,0.3)",
    color: "#1a7a37",
  },
  dot: { width: 8, height: 8, borderRadius: "50%", background: "var(--success)", flexShrink: 0 },
  clearText: { fontSize: 14, fontWeight: 500 },
  error: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 18px", borderRadius: "var(--radius)",
    background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)",
  },
  warning: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 18px", borderRadius: "var(--radius)",
    background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.3)",
  },
  icon: { fontSize: 16, flexShrink: 0, marginTop: 1 },
  pills: { display: "flex", flexWrap: "wrap", gap: 8 },
  pillError: {
    display: "inline-flex", alignItems: "center", padding: "4px 12px",
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.22)",
    borderRadius: 20, fontSize: 13, fontWeight: 500, color: "#c0392b", textDecoration: "none",
  },
  pillWarning: {
    display: "inline-flex", alignItems: "center", padding: "4px 12px",
    background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.28)",
    borderRadius: 20, fontSize: 13, fontWeight: 500, color: "#92400e", textDecoration: "none",
  },
};

// ─── Stock Snippet ────────────────────────────────────────────────────────────

function formatMarketCap(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  return `${(n / 1_000).toFixed(1)}K`;
}

const EXCHANGE_META = {
  TA:  { flag: "🇮🇱", label: "TASE" },
  LSE: { flag: "🇬🇧", label: "LSE"  },
  TO:  { flag: "🇨🇦", label: "TSX"  },
  US:  { flag: "🇺🇸", label: "NYSE" },
};

function getExchange(symbol) {
  const parts = symbol.split(".");
  const suffix = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "US";
  return EXCHANGE_META[suffix] || EXCHANGE_META.US;
}

function StockLogo({ symbol, name, flag, logoFromApi }) {
  const ticker = symbol.split(".")[0];
  const logoUrl = logoFromApi || `https://assets.parqet.com/logos/symbol/${ticker}?format=png`;
  const fallbackLetter = (name || ticker).charAt(0).toUpperCase();
  const [failed, setFailed] = React.useState(false);

  return (
    <div style={{ position: "relative", width: 38, height: 38, flexShrink: 0 }}>
      {/* Main logo circle */}
      <div style={{
        width: 38, height: 38, borderRadius: "50%",
        background: "var(--bg-subtle)", border: "1px solid var(--border-light)",
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {!failed ? (
          <img
            src={logoUrl}
            alt={ticker}
            onError={() => setFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
          />
        ) : (
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)" }}>
            {fallbackLetter}
          </span>
        )}
      </div>
      {/* Flag badge — bottom-left overlap */}
      <div style={{
        position: "absolute", bottom: -4, left: -4,
        width: 20, height: 20, borderRadius: "50%",
        background: "var(--bg)", border: "1.5px solid var(--border-light)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, lineHeight: 1,
      }}>
        {flag}
      </div>
    </div>
  );
}

function StockSnippet({ stocksData, loading, trackedStocks, onStockAdded, onStockRemoved }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);

  const trackedSymbols = useMemo(() => new Set((trackedStocks || []).map((s) => s.symbol)), [trackedStocks]);

  // Debounced Yahoo search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/api/rates/stock-search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  // Focus input when search opens
  useEffect(() => {
    if (showSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [showSearch]);

  const handleAdd = async (result) => {
    try {
      await apiClient.post("/api/rates/tracked-stocks", {
        symbol: result.symbol, name: result.name, exchange: result.exchange,
      });
      invalidateTrackedStocksCache();
      setSearchQuery(""); setSearchResults([]); setShowSearch(false);
      if (onStockAdded) onStockAdded(result);
    } catch (err) { console.error("Failed to add stock:", err); }
  };

  const handleRemove = async (symbol) => {
    try {
      await apiClient.delete(`/api/rates/tracked-stocks/${symbol}`);
      invalidateTrackedStocksCache();
      if (onStockRemoved) onStockRemoved(symbol);
    } catch (err) { console.error("Failed to remove stock:", err); }
  };

  if (loading) {
    return <div style={{ padding: "16px 0", color: "var(--text-secondary)", fontSize: 13 }}>טוען מניות...</div>;
  }

  const stocksList = trackedStocks || [];

  // Most recent fetch timestamp across all stocks
  const latestTs = stocksList.reduce((acc, s) => {
    const doc = stocksData[s.symbol]?.data;
    const t = doc?.createdAt ? new Date(doc.createdAt).getTime() : 0;
    return t > acc ? t : acc;
  }, 0);
  const tsLabel = latestTs ? new Date(latestTs).toLocaleString("he-IL", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
  }) : null;

  return (
    <div>
      {/* Search toggle + timestamp row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, direction: "rtl" }}>
        {tsLabel && (
          <div style={{ fontSize: 10, color: "var(--text-secondary)",
            display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ opacity: 0.6 }}>⏱</span>
            <span>נכון ל-{tsLabel}</span>
          </div>
        )}
        {!tsLabel && <div />}
        <button
          onClick={() => { setShowSearch((v) => !v); setSearchQuery(""); setSearchResults([]); }}
          style={{
            border: "1px solid var(--border-light)", background: showSearch ? "var(--primary)" : "transparent",
            color: showSearch ? "#fff" : "var(--text-secondary)",
            borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
            cursor: "pointer", fontFamily: "var(--font)", transition: "all 0.15s",
          }}
        >
          {showSearch ? "סגור" : "+ הוסף מניה"}
        </button>
      </div>

      {/* Search bar */}
      {showSearch && (
        <div style={{ marginBottom: 10, direction: "rtl" }}>
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="חפש לפי שם חברה או סמל מניה..."
            dir="auto"
            style={{
              width: "100%", padding: "8px 12px", border: "1px solid var(--border-light)",
              borderRadius: 8, fontSize: 13, background: "var(--bg)", color: "var(--text)",
              outline: "none", fontFamily: "var(--font)", boxSizing: "border-box",
            }}
          />
          {searching && (
            <div style={{ padding: "8px 0", fontSize: 12, color: "var(--text-secondary)", textAlign: "center" }}>מחפש...</div>
          )}
          {!searching && searchQuery.trim() && searchResults.length === 0 && (
            <div style={{ padding: "8px 0", fontSize: 12, color: "var(--text-secondary)", textAlign: "center" }}>לא נמצאו תוצאות</div>
          )}
          {searchResults.length > 0 && (
            <div style={{ maxHeight: 200, overflowY: "auto", borderRadius: 8,
              border: "1px solid var(--border-light)", marginTop: 6, background: "var(--bg)" }}>
              {searchResults.map((r) => {
                const already = trackedSymbols.has(r.symbol);
                return (
                  <div
                    key={r.symbol}
                    onClick={() => !already && handleAdd(r)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", cursor: already ? "default" : "pointer",
                      opacity: already ? 0.5 : 1, borderBottom: "1px solid var(--border-light)",
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) => { if (!already) e.currentTarget.style.background = "rgba(128,128,128,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-secondary)", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                        {r.symbol} · {r.exchange}
                      </div>
                    </div>
                    <span style={{ fontSize: 18, fontWeight: 600, color: already ? "var(--text-secondary)" : "var(--primary)",
                      flexShrink: 0, marginRight: 8 }}>
                      {already ? "✓" : "+"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stock list */}
      {stocksList.map((s, i) => {
        const doc   = stocksData[s.symbol]?.data;
        const price = doc?.price ?? null;
        const absDelta = price != null && doc?.previousClose != null ? price - doc.previousClose : null;
        const pctDelta = doc?.changePct ?? null;
        const up        = pctDelta != null && pctDelta >= 0;
        const changeClr = pctDelta == null ? "var(--text-secondary)" : up ? "#16a34a" : "#dc2626";
        const exch      = getExchange(s.symbol);
        const mcap      = formatMarketCap(doc?.marketCap);

        return (
          <div key={s.symbol} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "9px 0",
            borderBottom: i < stocksList.length - 1 ? "1px solid var(--border-light)" : "none",
            direction: "rtl",
          }}>
            {/* Clickable area: logo + name + price → asset detail page */}
            <Link
              to={`/asset/stock/${s.symbol}`}
              style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, textDecoration: "none", color: "inherit" }}
            >
              {/* Logo + flag badge */}
              <StockLogo symbol={s.symbol} name={s.name} flag={exch.flag} logoFromApi={doc?.logoUrl} />

              {/* Name + ticker · exchange */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 1 }}>
                  {s.symbol.split(".")[0]}
                  {mcap && <span style={{ marginRight: 6, opacity: 0.7 }}>· {mcap}</span>}
                </div>
              </div>

              {/* Price + Yahoo-style change */}
              <div style={{ textAlign: "left", flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", textAlign: "right" }}>
                  {price == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: doc?.currency || "ILS", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)}
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, color: changeClr, textAlign: "right", marginTop: 2 }}>
                  {pctDelta == null ? "—" : (
                    <>
                      {up ? "▲" : "▼"}{" "}
                      {Math.abs(absDelta).toFixed(2)}{" "}
                      ({up ? "+" : ""}{pctDelta.toFixed(2)}%)
                    </>
                  )}
                  {doc?.stale && <span title="המחיר החי מתעכב"> ⚠</span>}
                </div>
              </div>
            </Link>

            {/* Remove button — outside the link */}
            <button
              onClick={() => handleRemove(s.symbol)}
              title="הסר מהמעקב"
              style={{
                border: "none", background: "transparent", cursor: "pointer",
                color: "var(--text-secondary)", fontSize: 14, padding: "2px 4px",
                borderRadius: 4, opacity: 0.3, transition: "opacity 0.15s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#dc2626"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.3"; e.currentTarget.style.color = "var(--text-secondary)"; }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accentColor, onClick, title }) {
  return (
    <div
      className="kpi-card"
      onClick={onClick}
      title={title}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderTop: accentColor ? `3px solid ${accentColor}` : undefined,
        transition: "box-shadow 0.15s, transform 0.15s",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = "var(--shadow-md, 0 4px 16px rgba(0,0,0,0.1))";
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "";
      }}
    >
      <span className="kpi-label" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>
      <span className="kpi-value" style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
        {value}
      </span>
      {sub && (
        <span style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4, marginTop: 2 }}>
          {sub}
        </span>
      )}
    </div>
  );
}

// ─── Upcoming Events ──────────────────────────────────────────────────────────

const HORIZON_OPTIONS = [15, 30, 60, 90];

function UpcomingPanel({ loans, horizon }) {
  const now = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const items = useMemo(() => {
    const horizonDate = new Date(now);
    horizonDate.setDate(horizonDate.getDate() + horizon);
    const list = [];
    loans.forEach((loan) => {
      if (loan.maturityDate) {
        const d = new Date(loan.maturityDate);
        d.setHours(0,0,0,0);
        if (d >= now && d <= horizonDate) {
          list.push({
            id: `mat-${loan._id}`, loanId: loan._id, date: d,
            label: loan.lender || "הלוואה",
            amount: Number(loan.remainingPrincipal ?? loan.principal) || null,
            currency: loan.currency || "ILS",
          });
        }
      }
    });
    return list.sort((a, b) => a.date - b.date);
  }, [loans, now, horizon]);

  if (items.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "32px 16px", color: "var(--text-secondary)", gap: 8 }}>
        <span style={{ fontSize: 28, opacity: 0.4 }}>✓</span>
        <p style={{ margin: 0, fontSize: 14 }}>{`אין אירועים ב-${horizon} הימים הקרובים`}</p>
      </div>
    );
  }

  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((item) => {
        const days = Math.round((item.date - now) / (1000 * 60 * 60 * 24));
        const urgent = days <= 14;
        return (
          <li key={item.id}>
            <Link to={`/loan/${item.loanId}`} className="no-underline dash-upcoming-item" style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
              background: "var(--bg-subtle)", borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border-light)", textDecoration: "none",
              transition: "border-color 0.15s, background 0.15s",
            }}>
              {/* Days badge */}
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", width: 48, height: 48, flexShrink: 0,
                borderRadius: "var(--radius-sm)",
                background: urgent ? "rgba(239,68,68,0.1)" : "rgba(99,91,255,0.1)",
                border: `1px solid ${urgent ? "rgba(239,68,68,0.2)" : "rgba(99,91,255,0.15)"}`,
              }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: urgent ? "var(--error)" : "var(--accent)", lineHeight: 1 }}>{days}</span>
                <span style={{ fontSize: 10, color: "var(--text-secondary)", marginTop: 2 }}>ימים</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                  פירעון סופי · {formatDate(item.date)}
                </div>
              </div>
              {item.amount != null && (
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", flexShrink: 0, whiteSpace: "nowrap" }}>
                  {formatNum(item.amount)} {item.currency === "ILS" ? "₪" : item.currency}
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Debt by Lender Chart ────────────────────────────────────────────────────

function DebtChart({ loans, navigate }) {
  const data = useMemo(() => {
    const by = {};
    loans.forEach((l) => {
      const name = l.lender || "ללא שם";
      const val = Number(l.remainingPrincipal ?? l.principal) || 0;
      by[name] = (by[name] || 0) + val;
    });
    return Object.entries(by).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [loans]);

  if (data.length === 0) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
      height: 200, color: "var(--text-secondary)", fontSize: 14 }}>
      אין נתוני הלוואות
    </div>
  );

  const ROW_H = 44;
  const chartH = Math.max(180, data.length * ROW_H);

  return (
    <div style={{ display: "flex", direction: "rtl", gap: 0 }}>
      {/* Lender name column — right side (RTL start) */}
      <div style={{ width: 130, flexShrink: 0, display: "flex", flexDirection: "column",
        paddingTop: 20 /* offset for XAxis on top */ }}>
        {data.map((row, i) => (
          <div
            key={row.name}
            onClick={() => navigate(`/loans?lender=${encodeURIComponent(row.name)}`)}
            style={{
              height: ROW_H, display: "flex", alignItems: "center",
              fontSize: 13, fontWeight: 500, color: "var(--text)",
              cursor: "pointer", paddingRight: 4,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
            title={row.name}
          >
            {row.name}
          </div>
        ))}
      </div>

      {/* Bar chart — no Y axis */}
      <div style={{ flex: 1, cursor: "pointer" }}>
        <ResponsiveContainer width="100%" height={chartH}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
            <XAxis type="number" orientation="top"
              tickFormatter={(v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v/1_000).toFixed(0)}K` : String(v)}
              tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              formatter={(v) => [`${formatNum(v)} ₪`, "חוב נותר"]}
              contentStyle={{ fontSize: 13, borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border-light)", background: "var(--bg)", color: "var(--text)" }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={26}
              style={{ cursor: "pointer" }}
              onClick={(row) => { if (row?.name) navigate(`/loans?lender=${encodeURIComponent(row.name)}`); }}>
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} opacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Theme Switcher ───────────────────────────────────────────────────────────

const THEMES = [
  { id: "a", label: "A", name: "Purple", color: "#635bff" },
  { id: "b", label: "B", name: "Blue",   color: "#0071e3" },
  { id: "c", label: "C", name: "Dark",   color: "#7c3aed", dark: true },
];

function ThemeSwitcher({ collapsed }) {
  const { theme, setTheme } = useTheme();
  return (
    <div style={{ padding: "20px 12px 8px", borderTop: "1px solid var(--border-light)", marginTop: "auto" }}>
      {!collapsed && (
        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase",
          letterSpacing: "0.08em", marginBottom: 10, paddingRight: 2 }}>
          עיצוב · Prototype
        </div>
      )}
      <div style={{ display: "flex", gap: 6, justifyContent: collapsed ? "center" : "flex-start" }}>
        {THEMES.map((t) => (
          <button
            key={t.id}
            onClick={() => setTheme(t.id)}
            title={t.name}
            style={{
              flex: 1,
              height: 36,
              border: theme === t.id
                ? `2px solid ${t.color}`
                : "2px solid transparent",
              borderRadius: "var(--radius-sm)",
              background: t.dark ? "#0f0f14" : t.color === "#635bff" ? "#f6f9fc" : "#f5f5f7",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              transition: "border-color 0.15s",
              outline: "none",
              padding: 0,
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: "50%",
              background: t.color,
              boxShadow: theme === t.id ? `0 0 0 2px ${t.color}40` : "none",
              transition: "box-shadow 0.15s",
            }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: t.dark ? "#888" : "#555", letterSpacing: "0.04em" }}>
              {t.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const user = useCurrentUser();
  const [loans, setLoans] = useState([]);
  const [facilitySummary, setFacilitySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(60);
  const [stocksData, setStocksData] = useState({});
  const [stocksLoading, setStocksLoading] = useState(true);
  const [fxRates, setFxRates] = useState({});
  // Fetched and stored but not read by this component yet; the setter keeps the
  // request meaningful, so only the unread binding is dropped.
  const [, setFinancialSummary] = useState(null);
  const navigate = useNavigate();
  const [trackedStocks, setTrackedStocks] = useState([]);
  const [trackedLoading, setTrackedLoading] = useState(true);

  // Load tracked stocks
  const refreshTrackedStocks = () => {
    apiClient.get("/api/rates/tracked-stocks")
      .then((res) => { setTrackedStocks(res.data); setTrackedLoading(false); })
      .catch(() => setTrackedLoading(false));
  };

  useEffect(() => { refreshTrackedStocks(); }, []);

  function cycleHorizon() {
    setHorizon((prev) => {
      const idx = HORIZON_OPTIONS.indexOf(prev);
      return HORIZON_OPTIONS[(idx + 1) % HORIZON_OPTIONS.length];
    });
  }

  // Fetch prices when tracked stocks change
  useEffect(() => {
    if (trackedLoading || trackedStocks.length === 0) return;
    Promise.all(
      trackedStocks.map((s) =>
        apiClient.get(`/api/rates/stock/${s.symbol}`)
          .then((r) => ({ symbol: s.symbol, data: r.data, error: null }))
          .catch(() => ({ symbol: s.symbol, data: null, error: true }))
      )
    ).then((results) => {
      const map = {};
      results.forEach((r) => { map[r.symbol] = { data: r.data, error: r.error }; });
      setStocksData(map);
      setStocksLoading(false);
    });
  }, [trackedStocks, trackedLoading]);

  const handleStockAdded = (result) => {
    // Fetch price for the new stock immediately
    apiClient.get(`/api/rates/stock/${result.symbol}`)
      .then((r) => setStocksData((prev) => ({ ...prev, [result.symbol]: { data: r.data, error: null } })))
      .catch((e) => setStocksData((prev) => ({ ...prev, [result.symbol]: { data: null, error: e.message } })));
    refreshTrackedStocks();
  };

  const handleStockRemoved = (symbol) => {
    setTrackedStocks((prev) => prev.filter((s) => s.symbol !== symbol));
    setStocksData((prev) => { const n = { ...prev }; delete n[symbol]; return n; });
  };

  useEffect(() => {
    Promise.all([
      apiClient.get(`/loans`),
      apiClient.get(`/credit-facilities/summary`).catch(() => ({ data: null })),
      apiClient.get(`/api/rates/fx`).catch(() => ({ data: {} })),
      apiClient.get(`/financial-summary`).catch(() => ({ data: null })),
    ])
      .then(([lr, fr, fxr, fsr]) => {
        setLoans(Array.isArray(lr.data) ? lr.data : []);
        setFacilitySummary(fr.data);
        const rates = {};
        Object.entries(fxr.data || {}).forEach(([pair, doc]) => {
          const [from] = pair.split("/");
          if (from && doc.rate) rates[from] = Number(doc.rate);
        });
        setFxRates(rates);
        setFinancialSummary(fsr.data);
      })
      .catch(() => setLoans([]))
      .finally(() => setLoading(false));
  }, []);

  const activeLoans = useMemo(() => loans.filter((l) => l.status !== "repaid"), [loans]);

  const totalDebt = useMemo(
    () => computeTotalDebtILS(activeLoans, fxRates),
    [activeLoans, fxRates]
  );

  const portfolioLTV = useMemo(() => {
    let sw = 0, swLTV = 0;
    activeLoans.forEach((l) => {
      const ltv = Number(l.currentLTV);
      const p = Number(l.remainingPrincipal ?? l.principal) || 0;
      if (!Number.isNaN(ltv) && ltv > 0 && p > 0) { sw += p; swLTV += ltv * p; }
    });
    return sw > 0 ? swLTV / sw : null;
  }, [activeLoans]);

  const avgRate = useMemo(
    () => computeWeightedAvgRate(activeLoans, fxRates),
    [activeLoans, fxRates]
  );

  if (loading) {
    return (
      <div className="dashboard page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid var(--border)",
            borderTopColor: "var(--accent)", animation: "spin 0.8s linear infinite" }} />
          <span style={{ color: "var(--text-secondary)", fontSize: 14 }}>טוען נתוני תיק...</span>
        </div>
      </div>
    );
  }

  const utilColor = facilitySummary
    ? facilitySummary.overallUtilizationPct > 80 ? "#EF4444"
      : facilitySummary.overallUtilizationPct > 60 ? "#F59E0B"
      : "#10B981"
    : "#10B981";

  return (
    <div className="dashboard page" style={{ maxWidth: 1400 }}>

      {/* ── Hero / greeting ─────────────────────────────────── */}
      <div className="dash-hero" style={{
        padding: "24px 28px", marginBottom: 16, borderRadius: "var(--radius)",
        border: "1px solid var(--border-light)",
        background: "linear-gradient(135deg, rgba(var(--accent-rgb, 0,113,227),0.04) 0%, transparent 100%)",
      }}>
        <div style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 6 }}>
          {hebrewGreeting()} 👋
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px", fontSize: 14, color: "var(--text-secondary)" }}>
          <span>{hebrewDate()}</span>
          <span>·</span>
          <span>{activeLoans.length} הלוואות פעילות</span>
          {totalDebt > 0 && (
            <>
              <span>·</span>
              <span style={{ fontWeight: 600, color: "var(--text)" }}>₪{formatNum(totalDebt)} חוב כולל</span>
            </>
          )}
        </div>
      </div>

      {/* ── Alert banner ─────────────────────────────────────── */}
      {can(user, 'dashboard.alerts') && <div style={{ marginBottom: 24 }}>
        <AlertBanner loans={activeLoans} />
      </div>}

      {/* ── KPI row ──────────────────────────────────────────── */}
      <section className="dashboard-kpis" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", marginBottom: 24 }}>
        {can(user, 'dashboard.kpiTotalDebt') && <KpiCard
          label="חוב נותר כולל"
          value={totalDebt > 0 ? `₪${formatCompact(totalDebt)}` : "—"}
          title={totalDebt > 0 ? `₪${formatNum(totalDebt)}` : undefined}
          sub={`${activeLoans.length} הלוואות פעילות`}
          accentColor="#635BFF"
          onClick={() => navigate("/loans")}
        />}
        {can(user, 'dashboard.kpiAvgRate') && <KpiCard
          label="ריבית ממוצעת משוקללת"
          value={avgRate != null ? formatPct(avgRate) : "—"}
          sub="על בסיס קרן נותרת"
          accentColor="#0071e3"
        />}
        {can(user, 'dashboard.kpiAvgLtv') && <KpiCard
          label="LTV ממוצע משוקלל"
          value={portfolioLTV != null ? formatPct(portfolioLTV) : "—"}
          sub={portfolioLTV != null ? "הלוואות עם בטוחת מניות" : "אין בטוחות מניות"}
          accentColor={portfolioLTV != null && portfolioLTV > 70 ? "#EF4444" : "#10B981"}
          onClick={() => navigate("/collateral")}
        />}
        {can(user, 'dashboard.kpiUtilization') && <KpiCard
          label="ניצול מסגרות אשראי"
          value={facilitySummary ? formatPct(facilitySummary.overallUtilizationPct) : "—"}
          sub={facilitySummary
            ? `₪${formatNum(facilitySummary.totalDrawn)} מתוך ₪${formatNum(facilitySummary.totalCommitments)}`
            : "אין מסגרות אשראי"}
          accentColor={utilColor}
          onClick={() => navigate("/credit-facilities")}
        />}
      </section>

      {/* ── Two-column: upcoming + chart ─────────────────────── */}
      <section style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 20, marginBottom: 24, alignItems: "start" }}>
        {/* Upcoming events + stocks */}
        {(can(user, 'dashboard.upcomingEvents') || can(user, 'dashboard.stockPortfolio')) && <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {can(user, 'dashboard.upcomingEvents') && <>
            <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid var(--border-light)",
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>אירועים קרובים</h3>
              <span
                onClick={cycleHorizon}
                style={{ fontSize: 12, color: "var(--text-secondary)", background: "var(--bg-subtle)",
                  padding: "2px 10px", borderRadius: 20, border: "1px solid var(--border-light)",
                  cursor: "pointer", transition: "border-color 0.15s, color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-light)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              >
                {horizon} ימים
              </span>
            </div>
            <div style={{ padding: "12px 12px" }}>
              <UpcomingPanel loans={activeLoans} horizon={horizon} />
            </div>
          </>}

          {/* Stocks section below */}
          {can(user, 'dashboard.stockPortfolio') && <div style={{ borderTop: "1px solid var(--border-light)" }}>
            <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid var(--border-light)",
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>מניות תיק</h3>
            </div>
            <div style={{ padding: "12px 20px" }}>
              <StockSnippet stocksData={stocksData} loading={stocksLoading} trackedStocks={trackedStocks} onStockAdded={handleStockAdded} onStockRemoved={handleStockRemoved} />
            </div>
          </div>}
        </div>}

        {/* Debt by lender chart */}
        {can(user, 'dashboard.lenderChart') && <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid var(--border-light)" }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>חוב נותר לפי מלווה</h3>
          </div>
          <div style={{ padding: "16px 12px 12px" }}>
            <DebtChart loans={activeLoans} navigate={navigate} />
          </div>
        </div>}
      </section>

    </div>
  );
}

// Export the theme switcher for use in the sidebar
export { ThemeSwitcher };
